// src/lib/firebase.js
import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { initializeFirestore, enableMultiTabIndexedDbPersistence } from 'firebase/firestore';
import { getFunctions } from 'firebase/functions';
import { getStorage } from 'firebase/storage';

// Vite reads env vars from import.meta.env (set in .env.local)
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_SENDER_ID, // optional
  appId: import.meta.env.VITE_FIREBASE_APP_ID
};

// Initialize Firebase once
const app = initializeApp(firebaseConfig);

// Export ready-to-use handles
export const auth = getAuth(app);
export const db = initializeFirestore(app, {
  // Safari can stall Firestore WebChannel reads for about a minute before falling back.
  // This matches the door app and lets the SDK pick long-polling transport up front.
  experimentalAutoDetectLongPolling: true
});

// ✅ Cloud Functions (match your deployed region)
export const functions = getFunctions(app, 'australia-southeast2');

// ✅ Storage (brand logo / PWA icon uploads) — default bucket from VITE_FIREBASE_STORAGE_BUCKET
export const storage = getStorage(app);

// Warn if any required env is missing (helps during setup)
if (!firebaseConfig.apiKey || !firebaseConfig.authDomain || !firebaseConfig.projectId || !firebaseConfig.appId) {
  console.warn('[Firebase] Missing config values. Check your .env.local and restart `npm run dev`.');
}

// ✅ Enable IndexedDB persistence (multi-tab friendly)
enableMultiTabIndexedDbPersistence(db).catch((err) => {
  if (err.code === 'failed-precondition') {
    // Multiple tabs open AND browser doesn't support multi-tab persistence properly.
    console.warn('Firestore multi-tab persistence failed. Falling back to network-only.');
  } else if (err.code === 'unimplemented') {
    // Browser does not support persistence
    console.warn('Firestore persistence not supported by this browser.');
  }
});

// (Optional) export app if you ever need it elsewhere
export { app };
